import { useQuery } from "@apollo/client"
import { endOfDay, startOfDay, toTimestamp, nowDateTime, IANATimeZone } from "@hydrologiq/common/helpers/date"
import { TimePoint, convertTimeSerieData } from "@hydrologiq/common/types/graph/timeseries"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import { EnergyUnit } from "@hydrologiq/common/types/energy/consumption"
import FuelLevel, { MinWeightProps } from "../../../figures/FuelLevel"
import { DateTime } from "timezonecomplete"

export interface HydrogenFuelLevelFigureProps {
  title: string
  installationId: string
  installationStartDate: DateTime
  installationEndDate: DateTime
  installationMinPressure?: number
}

function HydrogenFuelLevelFigure({
  title,
  installationId,
  installationEndDate,
  installationStartDate,
  installationMinPressure,
}: HydrogenFuelLevelFigureProps) {
  const nowDate = nowDateTime()
  const installationEnded = nowDate.unixUtcMillis() >= installationEndDate.unixUtcMillis()
  const latestEndDate = installationEnded ? installationEndDate : nowDate

  const { loading, data, error } = useQuery(gqlTypes.HydrogenFuelLevelQueryDocument, {
    variables: {
      id: installationId,
      start: toTimestamp(startOfDay(installationStartDate), true),
      stop: toTimestamp(installationEnded ? endOfDay(latestEndDate) : latestEndDate, true),
      location: IANATimeZone(),
    },
  })

  let currentWeight: TimePoint | null = null
  let refuellingWeight: TimePoint | null = null
  let minWeight: MinWeightProps | undefined = undefined
  let offset: number | null = null
  if (data) {
    refuellingWeight = data.refuellingWeight ? convertTimeSerieData(data.refuellingWeight) : null
    currentWeight = data.weight ? convertTimeSerieData(data.weight) : null
    offset = data.weightMin ? convertTimeSerieData(data.weightMin).value : null

    if (offset !== null) {
      minWeight = { pressure: installationMinPressure, weight: offset }
    }
  }

  return (
    <FuelLevel
      heading={title}
      unit={EnergyUnit.kg}
      currentLevel={currentWeight ? currentWeight.value : null}
      maxLevel={refuellingWeight ? refuellingWeight.value : null}
      refuellingTime={refuellingWeight ? refuellingWeight.time : null}
      minWeight={minWeight}
      offset={offset}
      loading={loading}
      error={error ? error.message : undefined}
    />
  )
}

export default HydrogenFuelLevelFigure
